import { ArrowRight, Search, SlidersHorizontal, Sparkles, X } from 'lucide-react';
import { useMemo, useState, type CSSProperties } from 'react';
import { ClubBadge } from '../components/ClubBadge';
import { ElementBadge } from '../components/ElementBadge';
import { ElementIcon } from '../components/ElementIcon';
import { FilterSelect, type FilterOption } from '../components/FilterSelect';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { formations, techniqueById } from '../data';
import { playerRating } from '../competition';
import type { GameState } from '../gameState';
import type { Player } from '../types';

const statLabels: Record<string, string> = {
  kick: 'Tiro', dribble: 'Regate', pass: 'Pase', defense: 'Defensa',
  speed: 'Velocidad', power: 'Potencia', control: 'Control', stamina: 'Resistencia',
};

export function SquadPage({ game, onImprove }: { game: GameState; onImprove(playerId: string): void }) {
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState('ALL');
  const [element, setElement] = useState('ALL');
  const [squadArea, setSquadArea] = useState('ALL');
  const [selected, setSelected] = useState<Player | null>(null);
  const [sort, setSort] = useState<'name' | 'rating'>('rating');
  const benchIds = useMemo(() => new Set(game.save.benchPlayerIds), [game.save.benchPlayerIds]);

  const visible = useMemo(() => game.ownedPlayers
    .filter((player) => position === 'ALL' || player.position === position)
    .filter((player) => element === 'ALL' || player.element === element)
    .filter((player) => squadArea === 'ALL' || (squadArea === 'LINEUP' && game.save.lineup.includes(player.id)) || (squadArea === 'BENCH' && benchIds.has(player.id)) || (squadArea === 'RESERVE' && !game.save.lineup.includes(player.id) && !benchIds.has(player.id)))
    .filter((player) => `${player.name} ${player.metadata?.team ?? ''}`.toLocaleLowerCase('es').includes(query.toLocaleLowerCase('es')))
    .sort((a, b) => sort === 'name' ? a.name.localeCompare(b.name) : rating(b) - rating(a)),
  [benchIds, element, game.ownedPlayers, game.save.lineup, position, query, sort, squadArea]);
  const activeFormation = [...formations, ...game.save.customFormations].find((formation) => formation.id === game.save.formationId) ?? formations[0];
  const squadAverage = Math.round(game.ownedPlayers.reduce((sum, player) => sum + rating(player), 0) / Math.max(1, game.ownedPlayers.length));

  return (
    <div className="page squad-page">
      <header className="squad-hero card">
        <div className="squad-hero-intro">
          <span className="squad-hero-crest"><ClubBadge save={game.save} size="lg" /></span>
          <div><span className="eyebrow">PRIMER EQUIPO · {game.save.clubName.toLocaleUpperCase('es')}</span><h1>Plantilla</h1><p>Consulta atributos, afinidad y técnicas de cada jugador.</p><span className="squad-formation-name">SISTEMA ACTIVO <b>{activeFormation.name}</b></span></div>
        </div>
        <div className="squad-formation-visual" aria-label={`Formación activa: ${activeFormation.name}`}>
          <span className="squad-formation-lines" />
          {activeFormation.positions.map(([x, y], index) => <i style={{ left: `${y}%`, top: `${100 - x}%` }} key={index}>{index + 1}</i>)}
        </div>
        <div className="squad-hero-stats"><span><small>PLANTILLA</small><b>{game.ownedPlayers.length}</b><em>jugadores</em></span><span><small>ONCE</small><b>{game.save.lineup.length}</b><em>titulares</em></span><span><small>MEDIA</small><b>{squadAverage}</b><em>valoración</em></span></div>
      </header>

      <div className="toolbar card">
        <label className="search-field"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar jugador…" /></label>
        <div className="segmented">
          {['ALL', 'GK', 'DF', 'MF', 'FW'].map((item) => <button className={position === item ? 'active' : ''} onClick={() => setPosition(item)} key={item}>{item === 'ALL' ? 'Todos' : item}</button>)}
        </div>
        <div className="squad-filter-selects">
          <FilterSelect value={element} options={elementFilterOptions} onChange={setElement} ariaLabel="Filtrar plantilla por afinidad" />
          <FilterSelect value={squadArea} options={areaFilterOptions} onChange={setSquadArea} ariaLabel="Filtrar plantilla por grupo" />
        </div>
        <span className="squad-result-count"><b>{visible.length}</b> jugadores <small>mostrados</small></span>
        <span className="squad-sort"><SlidersHorizontal size={15} /><FilterSelect value={sort} options={sortOptions} onChange={(value) => setSort(value as 'name' | 'rating')} ariaLabel="Ordenar plantilla" /></span>
      </div>

      <div className={`squad-workspace ${selected ? 'has-inspector' : ''}`}>
        <section className="roster-table card">
          <div className="roster-head"><span>JUGADOR</span><span>POS.</span><span>ELEMENTO</span><span>MEDIA</span></div>
          <div className="roster-body">
            {visible.map((player, index) => (
              <button style={{ '--i': Math.min(index, 14) } as CSSProperties} className={`roster-row player-element--${player.element.toLowerCase()} ${selected?.id === player.id ? 'selected' : ''}`} onClick={() => setSelected(player)} key={player.id}>
                <span className="player-cell"><PlayerAvatar player={player} size="md" /><span><b>{player.name}</b><small>{player.metadata?.team ?? 'Aurora Eleven'}</small></span></span>
                <span><b className="position-tag">{player.position}</b></span>
                <ElementBadge element={player.element} />
                <strong className={rating(player) >= 78 ? 'rating-high' : ''}>{rating(player)}</strong>
              </button>
            ))}
          </div>
        </section>

        {selected && <aside className="player-inspector card">
          <button className="inspector-close" onClick={() => setSelected(null)} aria-label="Cerrar"><X size={18} /></button>
          <div className="inspector-identity"><PlayerAvatar player={selected} size="lg" /><div><span className="eyebrow">FICHA DEL JUGADOR</span><h2>{selected.name}</h2><p>{selected.position} · {selected.metadata?.team ?? 'Aurora Eleven'}</p></div></div>
          <div className="inspector-tags"><ElementBadge element={selected.element} /><span className="rating-pill">{rating(selected)} MED</span></div>
          <div className="stat-grid">
            {Object.entries(selected.stats).map(([key, value]) => <div className="stat-item" key={key}><span><small>{statLabels[key] ?? key}</small><b>{value}</b></span><div><i style={{ width: `${value}%` }} /></div></div>)}
          </div>
          <div className="technique-list"><span className="eyebrow">ARSENAL ACTUAL</span>{selected.techniques.length ? selected.techniques.slice(0, 3).map((id) => { const technique = techniqueById.get(id); return technique && <div className="technique-row" key={id}><i className={`element-bg--${technique.element.toLowerCase()}`} /><span><b>{technique.name}</b><small>{technique.type} · Potencia {technique.power}</small></span><strong>{technique.tp_cost} TP</strong></div>; }) : <p className="technique-empty">Este jugador no recibió técnicas al fichar.</p>}</div>
          <button className="inspector-improve-button" onClick={() => onImprove(selected.id)}>
            <Sparkles size={15} aria-hidden="true" />
            <span><small>SANTUARIO DE ESPÍRITUS</small><b>Mejorar</b></span>
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        </aside>}
      </div>
    </div>
  );
}

const elementFilterOptions: FilterOption[] = [
  { value: 'ALL', label: 'Todas las afinidades' },
  { value: 'FIRE', label: 'Fuego', prefix: <ElementIcon element="FIRE" size="sm" /> },
  { value: 'WIND', label: 'Viento', prefix: <ElementIcon element="WIND" size="sm" /> },
  { value: 'FOREST', label: 'Bosque', prefix: <ElementIcon element="FOREST" size="sm" /> },
  { value: 'MOUNTAIN', label: 'Montaña', prefix: <ElementIcon element="MOUNTAIN" size="sm" /> },
];
const areaFilterOptions: FilterOption[] = [
  { value: 'ALL', label: 'Toda la plantilla' },
  { value: 'LINEUP', label: 'Once titular' },
  { value: 'BENCH', label: 'Banquillo' },
  { value: 'RESERVE', label: 'Reserva' },
];
const sortOptions: FilterOption[] = [{ value: 'rating', label: 'Valoración' }, { value: 'name', label: 'Nombre' }];

function rating(player: Player) {
  return playerRating(player);
}
